import { useState, useRef, useEffect } from "react";
import { getImageUrl } from "@/utils/global";
import { ChevronDown, ChevronRight, Cake } from "lucide-react";
import { iNavBar, IProductoPersonalizado } from "@/types/app";

export default function DropdownNav({ navbar, navPersonalizados }: { navbar: iNavBar[], navPersonalizados: IProductoPersonalizado[] }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const pathname = decodeURIComponent(window.location.pathname);

    // Cerrar menú al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
                setActiveSubmenu(null);
            }
        };

        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen]);


    // Bloquear scroll de fondo cuando el menú está abierto (sin saltar al top)
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = '';
            };
        }
    }, [isMenuOpen]);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        if (!isMenuOpen) {
            setActiveSubmenu(null);
        }
    };

    const toggleSubmenu = (submenuName: string) => {
        setActiveSubmenu(activeSubmenu === submenuName ? null : submenuName);
    };

    return (
        <>
            {/* Overlay oscuro de fondo - como el modal del carrito */}
            {isMenuOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
                    onClick={() => {
                        setIsMenuOpen(false);
                        setActiveSubmenu(null);
                    }}
                ></div>
            )}

            <nav className="relative z-50 hidden xl:flex" ref={menuRef}>
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex items-center space-x-16 h-16">
                        {/* PRODUCTOS Dropdown - NUEVO DISEÑO CON CLICK */}
                        <div className="relative">
                            <button
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md border shadow-sm transition-all duration-300 group ${isMenuOpen
                                    ? "bg-[#790546] text-white border-[#790546] shadow-md"
                                    : "bg-white text-[#790546] border-[#790546]/25 hover:bg-[#790546] hover:text-white hover:shadow-md"
                                    }`}
                                onClick={toggleMenu}
                            >
                                <Cake className="w-5 h-5" />
                                <span className="text-sm font-semibold tracking-wide">Productos</span>
                                <ChevronDown
                                    className={`w-4 h-4 transition-all duration-300 ${isMenuOpen ? "rotate-180" : ""
                                        }`}
                                />
                            </button>

                            {/* Products Mega Dropdown - REDISEÑADO */}
                            {isMenuOpen && (
                                <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transform transition-all duration-300 ease-out z-50 w-[720px] flex flex-col">
                                    {/* Header fijo con botón de cerrar */}
                                    <div className="absolute top-0 right-0 p-3 z-[60] pointer-events-none">
                                        <button
                                            onClick={() => {
                                                setIsMenuOpen(false);
                                                setActiveSubmenu(null);
                                            }}
                                            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200 group shadow-md pointer-events-auto"
                                            aria-label="Cerrar menú"
                                        >
                                            <svg
                                                className="w-4 h-4 text-gray-600 group-hover:text-gray-800"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path d="M6 18L18 6M6 6l12 12"></path>
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Grid de 2 columnas para mejor organización */}
                                    <div className="grid grid-cols-2 relative">
                                        {/* Columna izquierda - Categorías principales con decoración */}
                                        <div className="relative p-4 pt-12 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-[#790546]/20 scrollbar-track-gray-100">
                                            {/* Fondo decorativo con pasteles */}
                                            <div
                                                className="absolute inset-0 opacity-[0.06] pointer-events-none"
                                                style={{
                                                    backgroundImage: `url(${getImageUrl("/images/patrones/fondo%20test.png")})`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                    backgroundRepeat: 'no-repeat'
                                                }}
                                            ></div>

                                            {/* Emojis decorativos de pasteles - sutiles pero visibles */}
                                            <div className="absolute top-6 left-6 text-3xl opacity-[0.12] pointer-events-none">🍰</div>
                                            <div className="absolute bottom-6 right-6 text-2xl opacity-[0.12] pointer-events-none">🧁</div>
                                            <div className="absolute top-1/2 left-8 text-xl opacity-[0.10] pointer-events-none">🍪</div>
                                            <div className="absolute top-1/3 right-8 text-xl opacity-[0.10] pointer-events-none">🎂</div>

                                            <div className="relative z-10">
                                                {navbar.map((item) => (
                                                    <div key={item.seccion.id} className="mb-2">
                                                        <button
                                                            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 font-medium group ${activeSubmenu === item.seccion.nombre
                                                                ? "bg-[#790546] text-white shadow-md"
                                                                : "text-gray-800 hover:bg-gray-50 hover:text-[#790546]"
                                                                }`}
                                                            onClick={() => toggleSubmenu(item.seccion.nombre)}
                                                        >
                                                            <span className="text-base">{item.seccion.nombre}</span>
                                                            {(item.subSeccion.length > 0 || item.seccion.id === 3) && (
                                                                <ChevronRight
                                                                    className={`w-4 h-4 transition-transform duration-200 ${activeSubmenu === item.seccion.nombre ? "rotate-90" : ""
                                                                        }`}
                                                                />
                                                            )}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Columna derecha - Subcategorías CON DISEÑO MORADO con efecto de ondas redondeadas en el borde izquierdo */}
                                        <div
                                            className="relative pl-10 pr-4 py-4 pt-12 bg-[#790546] max-h-[500px] overflow-y-auto overflow-x-hidden"
                                            style={{
                                                scrollbarWidth: 'thin',
                                                scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
                                                clipPath: 'polygon(8px 0%, 10px 3%, 12px 6%, 11px 10%, 9px 14%, 7px 18%, 6px 22%, 7px 26%, 9px 30%, 11px 34%, 12px 38%, 11px 42%, 9px 46%, 7px 50%, 6px 54%, 7px 58%, 9px 62%, 11px 66%, 12px 70%, 11px 74%, 9px 78%, 7px 82%, 6px 86%, 7px 90%, 9px 94%, 11px 97%, 10px 100%, 100% 100%, 100% 0%)'
                                            }}
                                        >
                                            {/* Estilos personalizados para scrollbar webkit (Chrome, Safari, Edge) */}
                                            <style>{`
                                            .relative::-webkit-scrollbar {
                                                width: 8px;
                                            }
                                            .relative::-webkit-scrollbar-track {
                                                background: transparent;
                                            }
                                            .relative::-webkit-scrollbar-thumb {
                                                background: rgba(255, 255, 255, 0.3);
                                                border-radius: 10px;
                                            }
                                            .relative::-webkit-scrollbar-thumb:hover {
                                                background: rgba(255, 255, 255, 0.5);
                                            }
                                        `}</style>

                                            {/* Fondo decorativo con pasteles - igual que el login */}
                                            <div
                                                className="absolute inset-0 opacity-[0.08] pointer-events-none"
                                                style={{
                                                    backgroundImage: `url(${getImageUrl("/images/patrones/fondo%20test.png")})`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                    backgroundRepeat: 'no-repeat',
                                                    filter: 'brightness(0) invert(1)'
                                                }}
                                            ></div>

                                            {/* Emojis decorativos de pasteles - más difuminados y parejos */}
                                            <div className="absolute top-6 right-6 text-3xl opacity-[0.12] pointer-events-none">🍰</div>
                                            <div className="absolute bottom-6 left-6 text-2xl opacity-[0.12] pointer-events-none">🧁</div>
                                            <div className="absolute top-1/2 right-8 text-xl opacity-[0.10] pointer-events-none">🍪</div>
                                            <div className="absolute top-1/3 left-8 text-xl opacity-[0.10] pointer-events-none">🎂</div>

                                            <div className="relative z-10">
                                                {activeSubmenu ? (
                                                    <>
                                                        {/* Header con el nombre de la categoría activa */}
                                                        <div className="mb-4 pb-3 border-b border-white/30">
                                                            <h4 className="text-sm font-bold text-white uppercase tracking-wider">
                                                                {activeSubmenu}
                                                            </h4>
                                                        </div>

                                                        {/* Lista de subcategorías */}
                                                        <div className="space-y-1">
                                                            {navbar.find(item => item.seccion.nombre === activeSubmenu)?.seccion.id === 3 ? (
                                                                // Productos personalizados
                                                                navPersonalizados?.map((personalizado) => (
                                                                    <a
                                                                        key={personalizado.id}
                                                                        href={`/producto/personalizado/${personalizado.nombre}`}
                                                                        className="block px-4 py-3 rounded-lg text-sm text-white bg-white/5 hover:bg-white hover:text-[#790546] transition-all duration-200 font-medium border border-white/10 hover:border-white/30 shadow-sm hover:shadow-lg hover:scale-[1.02] backdrop-blur-sm"
                                                                        onClick={() => {
                                                                            setIsMenuOpen(false);
                                                                            setActiveSubmenu(null);
                                                                        }}
                                                                    >
                                                                        {personalizado.nombre}
                                                                    </a>
                                                                ))
                                                            ) : (
                                                                // Subsecciones normales
                                                                navbar
                                                                    .find(item => item.seccion.nombre === activeSubmenu)
                                                                    ?.subSeccion.map((subseccion) => (
                                                                        <a
                                                                            key={subseccion.id}
                                                                            href={`/tienda/${activeSubmenu}/${subseccion.nombre}`}
                                                                            className="block px-4 py-3 rounded-lg text-sm text-white bg-white/5 hover:bg-white hover:text-[#790546] transition-all duration-200 font-medium border border-white/10 hover:border-white/30 shadow-sm hover:shadow-lg hover:scale-[1.02] backdrop-blur-sm"
                                                                            onClick={() => {
                                                                                setIsMenuOpen(false);
                                                                                setActiveSubmenu(null);
                                                                            }}
                                                                        >
                                                                            {subseccion.nombre}
                                                                        </a>
                                                                    ))
                                                            )}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="flex items-center justify-center h-full text-sm">
                                                        <div className="text-center">
                                                            <Cake className="w-12 h-12 mx-auto mb-2 opacity-40 text-white" />
                                                            <p className="font-medium text-white">Selecciona una categoría</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav>
        </>
    );
}
